/**
 * Input-Output Reference Service
 *
 * Loads and manages EnergyPlus Input-Output Reference documentation content.
 * This documentation describes how to use each EnergyPlus object, including
 * field-by-field descriptions, examples, and usage guidance.
 *
 * Documentation is converted from EnergyPlus LaTeX source using:
 *   python scripts/convert-energyplus-docs.py --doc-type input-output
 */

import type { EngineeringDocument } from '../types';

// Type for the index structure (same as engineering reference)
interface InputOutputIndex {
  documents: Array<{
    id: string;
    title: string;
    category: string;
    description: string;
    relatedObjects: string[];
    file: string;
  }>;
  categories: Array<{
    id: string;
    name: string;
    description: string;
  }>;
}

// Use Vite's import.meta.glob to dynamically import all markdown files
const markdownModules = import.meta.glob('../data/input-output-reference/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

// Try to import index.json - this is optional
const indexModules = import.meta.glob('../data/input-output-reference/index.json', {
  eager: true,
}) as Record<string, { default: InputOutputIndex } | InputOutputIndex>;

// Get the input-output index if it exists
function getInputOutputIndex(): InputOutputIndex | null {
  const indexKey = '../data/input-output-reference/index.json';
  const indexModule = indexModules[indexKey];

  if (!indexModule) {
    return null;
  }

  // Handle both default export and direct export
  if ('default' in indexModule) {
    return indexModule.default;
  }
  return indexModule as InputOutputIndex;
}

/**
 * Get content for a file by its filename
 */
function getContentByFilename(filename: string): string {
  const key = `../data/input-output-reference/${filename}`;
  return markdownModules[key] || '';
}

/**
 * Get all input-output reference documents
 * Returns documents using the same EngineeringDocument type for compatibility
 */
export function getInputOutputDocs(): EngineeringDocument[] {
  const index = getInputOutputIndex();
  if (!index) {
    return [];
  }

  return index.documents.map((doc) => ({
    id: doc.id,
    title: doc.title,
    category: doc.category,
    content: getContentByFilename(doc.file),
    relatedObjects: doc.relatedObjects,
  }));
}

/**
 * Get a specific input-output reference document by ID
 */
export function getInputOutputDoc(id: string): EngineeringDocument | null {
  const index = getInputOutputIndex();
  if (!index) {
    return null;
  }

  const docInfo = index.documents.find((d) => d.id === id);
  if (!docInfo) return null;

  return {
    id: docInfo.id,
    title: docInfo.title,
    category: docInfo.category,
    content: getContentByFilename(docInfo.file),
    relatedObjects: docInfo.relatedObjects,
  };
}

/**
 * Get input-output reference categories
 */
export function getInputOutputCategories(): Array<{
  id: string;
  name: string;
  description: string;
}> {
  const index = getInputOutputIndex();
  if (!index) {
    return [];
  }
  return index.categories;
}

/**
 * Get input-output docs by category
 */
export function getInputOutputDocsByCategory(category: string): EngineeringDocument[] {
  const index = getInputOutputIndex();
  if (!index) {
    return [];
  }

  return index.documents
    .filter((doc) => doc.category.toLowerCase() === category.toLowerCase())
    .map((doc) => ({
      id: doc.id,
      title: doc.title,
      category: doc.category,
      content: getContentByFilename(doc.file),
      relatedObjects: doc.relatedObjects,
    }));
}

/**
 * Find input-output docs related to an object type
 */
export function getInputOutputDocsForObject(objectType: string): EngineeringDocument[] {
  const index = getInputOutputIndex();
  if (!index) {
    return [];
  }

  const lowerObjectType = objectType.toLowerCase();
  return index.documents
    .filter((doc) =>
      doc.relatedObjects.some(
        (obj) => obj.toLowerCase() === lowerObjectType
      )
    )
    .map((doc) => ({
      id: doc.id,
      title: doc.title,
      category: doc.category,
      content: getContentByFilename(doc.file),
      relatedObjects: doc.relatedObjects,
    }));
}

/**
 * Check if input-output reference docs are available
 */
export function hasInputOutputDocs(): boolean {
  const index = getInputOutputIndex();
  return index !== null && index.documents.length > 0;
}
